import {
  EditOpportunityStage,
  GetOpportunityStage,
  RegisterOpportunityStage,
} from "@/dtos/OpportunityStage.dto";
import { StateObject } from "@/dtos/State.dto";
import OpportunityStage from "@/services/opportunity/opportunity-stage";
import {
  CLEAR_OPPORTUNITY_STAGE_ISLOADING,
  DELETE_OPPORTUNITY_STAGE,
  EDIT_OPPORTUNITY_STAGE,
  GET_OPPORTUNITY_STAGES,
  REGISTER_OPPORTUNITY_STAGE,
} from "@/store/action-types";
import {
  SET_ERRORMSG,
  SET_OPPORTUNITY_STAGE,
  SET_OPPORTUNITY_STAGES,
} from "@/store/mutation-types";
import { AxiosResponse } from "axios";
import { Module } from "vuex";

const opportunityStage: Module<any, {}> = {
  state: {
    opportunityStage: {
      data: null,
      isLoading: false,
    },
    opportunityStages: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    opportunityStage: (state: { opportunityStage: any }) => state.opportunityStage,
    opportunityStages: (state: { opportunityStages: any }) => state.opportunityStages,
  },
  mutations: {
    [SET_OPPORTUNITY_STAGE]: (
      state: { opportunityStage: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.opportunityStage.data = payload.data;
      state.opportunityStage.isLoading = payload.isLoading;
    },
    [SET_OPPORTUNITY_STAGES]: (
      state: { opportunityStages: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.opportunityStages.data = payload.data;
      state.opportunityStages.isLoading = payload.isLoading;
    },
  },
  actions: {
    [REGISTER_OPPORTUNITY_STAGE]: async ({ commit }: any, payload: any) => {
      commit(SET_OPPORTUNITY_STAGE, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await OpportunityStage.registerOpportunityStage(
          payload as RegisterOpportunityStage
        );

        if (result) {
          if (result.data.status) {
            commit(SET_OPPORTUNITY_STAGE, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_OPPORTUNITY_STAGE, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_OPPORTUNITY_STAGE, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_OPPORTUNITY_STAGE, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please try again");
        } else if (error.response?.status === 500) {
          commit(SET_ERRORMSG, "Server error occurred");
        } else if (error.response?.status === 404) {
          commit(SET_ERRORMSG, "Resource not found");
        } else if (!error.response) {
          commit(SET_ERRORMSG, "Network connection error");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to register opportunity stage"
          );
        }

        return false;
      }
    },
    [GET_OPPORTUNITY_STAGES]: async ({ commit }: any, payload: any) => {
      commit(SET_OPPORTUNITY_STAGES, { isLoading: true });

      try {
        const result: AxiosResponse = await OpportunityStage.getOpportunityStages(
          payload as GetOpportunityStage
        );

        if (result) {
          if (result.data.status) {
            commit(SET_OPPORTUNITY_STAGES, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_OPPORTUNITY_STAGES, { isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_OPPORTUNITY_STAGES, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_OPPORTUNITY_STAGES, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please try again");
        } else if (error.response?.status === 500) {
          commit(SET_ERRORMSG, "Server error occurred");
        } else if (error.response?.status === 404) {
          commit(SET_ERRORMSG, "Opportunity stages not found");
        } else if (!error.response) {
          commit(SET_ERRORMSG, "Network connection error");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to load opportunity stages"
          );
        }

        return false;
      }
    },
    [EDIT_OPPORTUNITY_STAGE]: async ({ commit }: any, payload: any) => {
      commit(SET_OPPORTUNITY_STAGE, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await OpportunityStage.editOpportunityStage(
          payload as EditOpportunityStage
        );

        if (result) {
          if (result.data.status) {
            commit(SET_OPPORTUNITY_STAGE, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_OPPORTUNITY_STAGE, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_OPPORTUNITY_STAGE, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_OPPORTUNITY_STAGE, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please try again");
        } else if (error.response?.status === 500) {
          commit(SET_ERRORMSG, "Server error occurred");
        } else if (error.response?.status === 404) {
          commit(SET_ERRORMSG, "Resource not found");
        } else if (!error.response) {
          commit(SET_ERRORMSG, "Network connection error");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to update opportunity stage"
          );
        }

        return false;
      }
    },
    [DELETE_OPPORTUNITY_STAGE]: async ({ commit }: any, payload: string) => {
      commit(SET_OPPORTUNITY_STAGE, { data: { id: payload }, isLoading: true });

      try {
        const result: AxiosResponse = await OpportunityStage.deleteOpportunityStage(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_OPPORTUNITY_STAGE, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_OPPORTUNITY_STAGE, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_OPPORTUNITY_STAGE, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_OPPORTUNITY_STAGE, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please try again");
        } else if (error.response?.status === 500) {
          commit(SET_ERRORMSG, "Server error occurred");
        } else if (error.response?.status === 404) {
          commit(SET_ERRORMSG, "Resource not found");
        } else if (!error.response) {
          commit(SET_ERRORMSG, "Network connection error");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to delete opportunity stage"
          );
        }

        return false;
      }
    },
    [CLEAR_OPPORTUNITY_STAGE_ISLOADING]: ({ commit }) => {
      commit(SET_OPPORTUNITY_STAGE, { isLoading: false });
      commit(SET_OPPORTUNITY_STAGES, { isLoading: false });
    },
  },
};

export default opportunityStage;
